// Shared Excel (.xlsx) import machinery — the sample-file writer and the
// sheet reader behind Purchase's and Opening Stock's imports. Each page
// keeps its own column spec (one `XlsxCol[]` per sheet, in the same
// left-to-right order as that page's own grid) and its own row → item
// mapping; this file only knows how to turn a spec into a styled workbook
// with dropdowns, and how to read a sheet back by header name.
//
// `exceljs` is imported dynamically so its ~930 KB chunk only loads when
// someone actually downloads a sample or imports a file.

import type { Cell, DataValidation, Workbook } from 'exceljs';

export interface XlsxCol<F extends string = string> {
  header:   string;
  field:    F;
  width:    number;
  /** Key into the `lists` passed to downloadImportWorkbook — becomes an in-cell dropdown. */
  list?:    string;
  numeric?: boolean;
  date?:    boolean;
  aliases?: string[];
  /** Filled by an Excel formula in the sample (greyed, not meant to be typed). */
  formula?: (r: number, col: (f: F) => string) => string;
  note?:    string;
  /** Header fill (ARGB) — overrides the default indigo, e.g. to set a group of columns apart. */
  headerArgb?: string;
}

export type XlsxSampleRow<F extends string = string> = Partial<Record<F, string | number | Date>>;

export interface XlsxSheet<F extends string = string> {
  name:     string;
  cols:     XlsxCol<F>[];
  sample:   XlsxSampleRow<F>[];
  tabArgb:  string;
}

export const XLSX_HEADER_ARGB  = 'FF4F46E5';   // indigo — ordinary input columns
export const XLSX_GROUP_ARGB   = 'FF0F766E';   // teal — a set-apart group (e.g. invoice header)
const FORMULA_HEAD_ARGB = 'FFE2E8F0';
const FORMULA_CELL_ARGB = 'FFF1F5F9';
const FORMULA_TEXT_ARGB = 'FF475569';

export function xlsxColLetter(n: number): string {   // 1 → A, 27 → AA
  let s = '';
  for (; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

// 'YYYY-MM-DD' → a UTC Date, which ExcelJS writes as that exact calendar day.
export const xlsxUtcDate = (iso: string) => new Date(`${iso}T00:00:00Z`);

export async function downloadImportWorkbook<F extends string>(opts: {
  fileName:   string;
  sheets:     XlsxSheet<F>[];
  lists:      Record<string, string[]>;
  readme:     string[];
  /** Lists whose sample values must NOT be swapped for a list option (e.g. company/broker names). */
  freeLists?: string[];
  /** Rows pre-filled with formulas, number formats and dropdowns. */
  rows?:      number;
}) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'DiaWolt';
  const nRows = opts.rows ?? 300;

  // Dropdown sources live on a hidden "Lists" sheet (added last, so it's not
  // the first tab) — inline list formulas are capped at 255 characters,
  // which a long company/broker list or custom colours would blow.
  const listKeys = Object.keys(opts.lists).filter(k => opts.lists[k].length > 0);
  const listRef: Record<string, string> = {};
  listKeys.forEach((k, i) => {
    const L = xlsxColLetter(i + 1);
    listRef[k] = `Lists!$${L}$1:$${L}$${opts.lists[k].length}`;
  });

  for (const sheet of opts.sheets) {
    const { cols } = sheet;
    const ws = wb.addWorksheet(sheet.name, {
      views: [{ state: 'frozen', ySplit: 1 }],
      properties: { tabColor: { argb: sheet.tabArgb } },
    });
    ws.columns = cols.map(c => ({ header: c.header, key: c.field, width: c.width }));
    const colOf = (f: F) => xlsxColLetter(cols.findIndex(c => c.field === f) + 1);

    const head = ws.getRow(1);
    head.height = 22;
    head.eachCell((cell, n) => {
      const c = cols[n - 1];
      cell.font = { bold: true, color: { argb: c.formula ? FORMULA_TEXT_ARGB : 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: c.formula ? FORMULA_HEAD_ARGB : c.headerArgb ?? XLSX_HEADER_ARGB } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      const note = c.formula ? 'Calculated automatically — no need to type here' : c.note;
      if (note) cell.note = note;
    });

    // Example rows: a value that isn't in its column's dropdown (e.g. "H"
    // when this business uses custom loose colours like "G-H") is swapped
    // for an option from that list, so the sample never contradicts itself.
    sheet.sample.forEach((s, r) => ws.addRow(cols.map(c => {
      if (c.formula) return null;
      const v = s[c.field] ?? null;
      const opts2 = c.list && !opts.freeLists?.includes(c.list) ? opts.lists[c.list] : null;
      if (v != null && opts2?.length && !opts2.includes(String(v))) return opts2[Math.min(r + 1, opts2.length - 1)];
      return v;
    })));

    for (let r = 2; r <= nRows + 1; r++) {
      cols.forEach((c, i) => {
        const cell = ws.getCell(`${xlsxColLetter(i + 1)}${r}`);
        if (c.formula) {
          cell.value = { formula: c.formula(r, colOf) };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FORMULA_CELL_ARGB } };
          cell.font = { color: { argb: FORMULA_TEXT_ARGB } };
        }
        if (c.date) cell.numFmt = 'dd-mmm-yyyy';
        else if (c.numeric) cell.numFmt = c.field === 'carat' ? '0.00' : '#,##0.##';
      });
    }

    // One dropdown rule per column range. Setting cell.dataValidation per cell
    // instead makes ExcelJS write overlapping ranges, which Excel reports as a
    // damaged file. ('warning', not 'stop' — a non-standard value is still
    // allowed; the importer keeps it as typed and flags it.)
    const validations = (ws as unknown as { dataValidations: { add(range: string, v: DataValidation): void } }).dataValidations;
    cols.forEach((c, i) => {
      const ref = c.list && listRef[c.list];
      if (!ref) return;
      const L = xlsxColLetter(i + 1);
      validations.add(`${L}2:${L}${nRows + 1}`, {
        type: 'list', allowBlank: true, formulae: [ref],
        showErrorMessage: true, errorStyle: 'warning',
        errorTitle: c.header, error: `"${c.header}" isn't in the saved list. Keep it anyway?`,
      });
    });
  }

  const readme = wb.addWorksheet('How to use');
  readme.getColumn(1).width = 110;
  opts.readme.forEach((t, i) => {
    const cell = readme.getCell(`A${i + 1}`);
    cell.value = t;
    cell.alignment = { wrapText: true, vertical: 'top' };
    if (i === 0) cell.font = { bold: true, size: 14 };
  });

  const listSheet = wb.addWorksheet('Lists', { state: 'hidden' });
  listKeys.forEach((k, i) => {
    const L = xlsxColLetter(i + 1);
    opts.lists[k].forEach((v, r) => { listSheet.getCell(`${L}${r + 1}`).value = v; });
  });

  const buf = await wb.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const a = document.createElement('a');
  a.href = url; a.download = opts.fileName;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Reading ──────────────────────────────────────────────────────────────────

export async function loadWorkbook(data: ArrayBuffer): Promise<Workbook> {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(data);
  return wb;
}

/** Strip currency symbols, thousands separators and % — '' if what's left isn't a number. */
export function cleanNum(s: string): string {
  const t = s.replace(/[,\s$€₹%]/g, '');
  return t && !isNaN(parseFloat(t)) ? t : '';
}

const normHeader = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();

// Excel date cell / serial number / typed text → 'YYYY-MM-DD', or '' if it
// can't be read. ExcelJS hands real date cells back as UTC midnight, so the
// UTC parts are the calendar day. Typed text is read day-first (01/09/2026 =
// 1 Sep), unless the second number can't be a month.
export function parseImportDate(v: unknown): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const ymd = (y: number, m: number, d: number) => {
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d ? `${y}-${pad(m)}-${pad(d)}` : '';
  };
  if (v instanceof Date && !isNaN(v.getTime())) return ymd(v.getUTCFullYear(), v.getUTCMonth() + 1, v.getUTCDate());
  if (typeof v === 'number' && v > 20000 && v < 80000) {
    const dt = new Date(Math.round((v - 25569) * 86400000));
    return ymd(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
  }
  if (typeof v !== 'string' || !v.trim()) return '';
  const s = v.trim();
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (m) return ymd(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
  if (m) {
    const y = m[3].length === 2 ? 2000 + +m[3] : +m[3];
    const a = +m[1], b = +m[2];
    return b > 12 && a <= 12 ? ymd(y, a, b) : ymd(y, b, a);
  }
  const t = Date.parse(s);   // "1 Sep 2026", "Sep 1, 2026"…
  if (!isNaN(t)) { const dt = new Date(t); return ymd(dt.getFullYear(), dt.getMonth() + 1, dt.getDate()); }
  return '';
}

// The underlying value of a cell — formula results, rich text and
// hyperlinks unwrapped.
function cellRaw(cell: Cell): unknown {
  const v: unknown = cell.value;
  if (v && typeof v === 'object' && !(v instanceof Date)) {
    if ('result' in v) return (v as { result: unknown }).result;
    if ('richText' in v) return (v as { richText: { text: string }[] }).richText.map(t => t.text).join('');
    if ('text' in v) return (v as { text: unknown }).text;
    return null;
  }
  return v;
}
// Plain text of a cell. A cell formatted as a percentage stores a fraction
// (−35% → −0.35), so scale it back up.
function cellText(cell: Cell): string {
  const v = cellRaw(cell);
  if (v instanceof Date) return parseImportDate(v);
  if (v == null || typeof v === 'object') return '';
  if (typeof v === 'number' && (cell.numFmt ?? '').includes('%')) return String(+(v * 100).toFixed(6));
  return String(v).trim();
}

export interface XlsxRow<F extends string> {
  sheet: string;
  rowNo: number;
  /** Cell text by field ('' when the column is absent or empty). */
  text:  Partial<Record<F, string>>;
  /** Raw cell value by field — use for dates (parseImportDate). */
  raw:   Partial<Record<F, unknown>>;
}

/**
 * Every non-empty data row of one sheet, columns matched to `cols` by header
 * name (or an alias) — so order is free and optional columns can be
 * deleted. Looks the sheet up by exact name first, then by `match` on the
 * lower-cased name. Returns null if the sheet isn't there; a sheet whose
 * header row matches nothing adds a warning. A row counts as empty when
 * every typed (non-formula) column is blank.
 */
export function readSheetRows<F extends string>(
  wb: Workbook, sheetName: string, match: (lowerName: string) => boolean,
  cols: XlsxCol<F>[], warnings: string[],
): XlsxRow<F>[] | null {
  const ws = wb.getWorksheet(sheetName) ?? wb.worksheets.find(w => match(w.name.toLowerCase()));
  if (!ws) return null;

  // Left to right, each header takes the first spec column it matches that
  // no earlier header has taken. That lets two columns answer to the same
  // name: Purchase's paid column was "Price/ct" and the marked up one
  // "Cost/ct"; now it's the other way round, and both old and new files
  // still map right because the paid column always comes first.
  const fieldAt = new Map<number, XlsxCol<F>>();
  const taken = new Set<F>();
  ws.getRow(1).eachCell((cell, n) => {
    const h = normHeader(cellText(cell));
    const c = cols.find(c => !taken.has(c.field) && (normHeader(c.header) === h || c.aliases?.includes(h)));
    if (c) { fieldAt.set(n, c); taken.add(c.field); }
  });
  if (fieldAt.size === 0) { warnings.push(`"${ws.name}" sheet: header row not recognised — skipped.`); return []; }

  const out: XlsxRow<F>[] = [];
  ws.eachRow((row, rowNo) => {
    if (rowNo === 1) return;
    const text: Partial<Record<F, string>> = {};
    const raw:  Partial<Record<F, unknown>> = {};
    fieldAt.forEach((c, n) => {
      const cell = row.getCell(n);
      raw[c.field] = cellRaw(cell);
      text[c.field] = cellText(cell);
    });
    if (!cols.some(c => !c.formula && text[c.field])) return;
    out.push({ sheet: ws.name, rowNo, text, raw });
  });
  return out;
}

/** Case-insensitive match of a typed value against a list; warns and keeps it as typed if it isn't there. */
export function matchOption(v: string, options: readonly string[], label: string, where: string, warnings: string[]): string {
  if (!v) return '';
  const hit = options.find(o => o.toLowerCase() === v.toLowerCase());
  if (!hit) warnings.push(`${where}: ${label} "${v}" isn't a standard value — kept as typed.`);
  return hit ?? v;
}
